/**
 * EmbedPDF bridge that converts text selections and marquee crops into proofs.
 * @module medicalNecessityAnalysis/components/PdfProofCaptureBridge
 */
import { useInteractionManagerCapability } from "@embedpdf/plugin-interaction-manager/react";
import { useRenderCapability } from "@embedpdf/plugin-render/react";
import type { MarqueeEndEvent } from "@embedpdf/plugin-selection";
import { useSelectionCapability } from "@embedpdf/plugin-selection/react";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import React, { useEffect, useRef } from "react";
import { usePdfProofCapture } from "../context/PdfProofCaptureContext";

/** Props for {@link PdfProofCaptureBridge}. */
interface PdfProofCaptureBridgeProps {
  /** Active EmbedPDF document identifier. */
  documentId: string | null;
}

/**
 * Interaction-manager mode the selection plugin seeds for every document. Its
 * per-mode flags are the only gate on text selection and marquee dragging.
 */
const POINTER_MODE = "pointerMode";

/** Interaction-manager claim token for the screenshot crop cursor. */
const CROP_CURSOR_TOKEN = "proof-screenshot-crop";

/**
 * Priority of the crop cursor claim. The selection plugin claims the I-beam at
 * priority 10 whenever the pointer is over text, and it cannot release that
 * claim while text selection is disabled, so the crop cursor must outrank it.
 */
const CROP_CURSOR_PRIORITY = 100;

/**
 * Listens to EmbedPDF selection events inside the renderer provider.
 *
 * @param props - Active EmbedPDF document ID
 * @returns No visible UI; this component only bridges plugin events
 */
export const PdfProofCaptureBridge: React.FC<PdfProofCaptureBridgeProps> = ({
  documentId,
}) => {
  const { provides: selection } = useSelectionCapability();
  const { provides: renderer } = useRenderCapability();
  const { provides: interactionManager } = useInteractionManagerCapability();
  const { mode, receiveDraft } = usePdfProofCapture();
  const isProcessing = useRef(false);

  // Without the plugin capabilities every drag is silently ignored, so surface
  // it instead of leaving the reviewer waiting for a dialog that never opens.
  useEffect(() => {
    if (mode === "idle" || (selection && renderer)) return;
    toast.error("PDF capture is unavailable for this document");
  }, [mode, renderer, selection]);

  useEffect(() => {
    if (!selection || !documentId) return undefined;
    const isScreenshot = mode === "screenshot";
    // Marquee only starts when a drag does not land on selectable text, so
    // text selection has to be off for screenshots or the drag highlights
    // words instead of drawing a crop box.
    selection.enableForMode(
      POINTER_MODE,
      {
        enableSelection: !isScreenshot,
        showSelectionRects: !isScreenshot,
        enableMarquee: isScreenshot,
        showMarqueeRects: true,
      },
      documentId,
    );
    if (mode !== "text") selection.forDocument(documentId).clear();
    return () => {
      selection.enableForMode(
        POINTER_MODE,
        {
          enableSelection: true,
          showSelectionRects: true,
          enableMarquee: false,
          showMarqueeRects: true,
        },
        documentId,
      );
    };
  }, [documentId, mode, selection]);

  // Page cursors are inline styles owned by the interaction manager, so the
  // crop crosshair has to be claimed through it rather than set in CSS.
  useEffect(() => {
    if (!interactionManager || !documentId || mode !== "screenshot")
      return undefined;
    const scope = interactionManager.forDocument(documentId);
    scope.setCursor(CROP_CURSOR_TOKEN, "crosshair", CROP_CURSOR_PRIORITY);
    return () => scope.removeCursor(CROP_CURSOR_TOKEN);
  }, [documentId, interactionManager, mode]);

  useEffect(() => {
    if (!selection || mode !== "text") return undefined;
    return selection.onEndSelection(
      async ({ documentId: selectedDocument }) => {
        if (isProcessing.current) return;
        isProcessing.current = true;
        try {
          const scope = selection.forDocument(selectedDocument);
          const textParts = await scope.getSelectedText().toPromise();
          const text = textParts
            .map((part) => part.trim())
            .filter(Boolean)
            .join("\n");
          if (!text) {
            toast.error(
              "No selectable text was found. Try capturing a screenshot instead.",
            );
            return;
          }
          const boundingRect = scope.getBoundingRects()[0];
          if (!boundingRect) {
            toast.error(
              "No selectable text was found. Try capturing a screenshot instead.",
            );
            return;
          }
          receiveDraft({
            type: "pdf_text",
            text,
            region: { pageIndex: boundingRect.page, rect: boundingRect.rect },
          });
        } catch {
          toast.error("Failed to capture the selected text");
        } finally {
          isProcessing.current = false;
        }
      },
    );
  }, [mode, receiveDraft, selection]);

  useEffect(() => {
    if (!selection || !renderer || mode !== "screenshot") return undefined;
    return selection.onMarqueeEnd(
      async ({
        documentId: selectedDocument,
        pageIndex,
        rect,
      }: MarqueeEndEvent) => {
        if (isProcessing.current) return;
        isProcessing.current = true;
        try {
          const blob = await renderer
            .forDocument(selectedDocument)
            .renderPageRect({
              pageIndex,
              rect,
              options: {
                scaleFactor: 2,
                dpr: 1,
                imageType: "image/png",
              },
            })
            .toPromise();
          receiveDraft({
            type: "pdf_screenshot",
            blob,
            previewUrl: URL.createObjectURL(blob),
            region: { pageIndex, rect },
          });
        } catch {
          toast.error("Failed to capture the selected PDF region");
        } finally {
          isProcessing.current = false;
        }
      },
    );
  }, [mode, receiveDraft, renderer, selection]);

  return null;
};
